<template>
    <div>
        <draggable :list="inspectionTestPlan.sections" draggable=".draggable" @change="sectionsReordered"
                   class="flex flex-wrap items-center -m-3">
            <div v-for="(section,index) in inspectionTestPlan.sections" class="m-1 draggable">
                <div v-if="index === activeSectionIndex" class="flex">
                    <div class="relative">
                        <autoresizable-input measure="font-medium text-sm" ref="resizer" v-if="editable">
                            <input
                                placeholder="..."
                                type="text"
                                v-model="section.title"
                                @input="inspectionTestPlan.update()"
                                @change="inspectionTestPlan.addSnapshot()"
                                @keydown.enter.prevent="$emit('change', $event.target.value);$event.target.blur()"
                                class="relative inline-block box-content m-0 border-0 shadow-none px-3 pl-2 font-medium text-sm rounded-l-md bg-blue-100 text-blue-700 focus:z-20 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                style="min-width: 20px;"
                                :class="section.optional ? 'pr-20' : 'pr-2'"
                            >
                        </autoresizable-input>
                        <button v-else class="px-3 py-2 font-medium text-sm rounded-md bg-blue-100 text-blue-700"
                                v-text="section.title"></button>
                    </div>
                </div>
                <div v-else class="flex">
                    <div class="relative" @click="select(index)" style="cursor: pointer">
                        <autoresizable-input measure="font-medium text-sm" ref="resizer" v-if="editable">
                            <input
                                @click="select(index)"
                                placeholder="..."
                                type="text"
                                v-model="section.title"
                                class="relative inline-block box-content m-0 border-0 shadow-none px-3 pl-2 font-medium text-sm rounded-l-md focus:z-20 focus:outline-none focus:ring-2"
                                style="min-width: 20px;cursor: pointer"
                                :class="section.optional ? 'pr-20' : 'pr-2'"
                            >
                        </autoresizable-input>
                        <button v-else
                                class="px-3 py-2 font-medium text-sm rounded-md text-gray-500 hover:text-gray-700"
                                v-text="section.title"></button>
                    </div>
                    <ui-dropdown menu-class="p-3 w-48" class="flex" trigger-class="flex" v-if="editable">
                        <template #default="{ toggle }">
                            <button @click="toggle"
                                    class="p-2 font-medium text-xs rounded-r-md text-gray-300 focus:outline-none">
                                <i class="fa fa-chevron-down fa-fw"></i>
                            </button>
                        </template>

                        <template #menu="{ close }">
                            <ui-dropdown-item v-if="! inspectionTestPlan.cannot_be_edited_by_authed_user"
                                              icon="fas fa-copy"
                                              @click="duplicateSection(section); close();">
                                Duplicate
                            </ui-dropdown-item>
                            <ui-dropdown-item v-if="! inspectionTestPlan.cannot_be_edited_by_authed_user"
                                              icon="fas fa-trash"
                                              danger @click="deleteSection(section)">Delete
                            </ui-dropdown-item>
                        </template>
                    </ui-dropdown>
                </div>
            </div>
            <div slot="header" class="m-3">
                <button
                    class="px-3 py-2 font-medium text-sm rounded-md"
                    :class="isPage('properties') ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'"
                    @click="selectPage('properties')"
                >Properties
                </button>
            </div>
            <div slot="footer" class="m-3" v-if="editable">
                <button
                    class="group px-3 py-2 inline-flex items-center space-x-2 font-medium text-sm rounded-md text-gray-400 hover:text-gray-700"
                    @click="add">
                    <i class="fa fa-plus text-sm text-gray-300 group-hover:text-gray-500"></i>
                    <div>New</div>
                </button>
            </div>
        </draggable>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import {InspectionTestPlan} from "../../models";
import {cloneDeep} from "lodash";

export default {
    components: {
        draggable
    },
    props: {
        inspectionTestPlan: InspectionTestPlan,
        editable: true
    },
    data() {
        return {
            activeSectionIndex: null,
            activePage: 'properties',
        }
    },
    methods: {
        add() {
            const newSection = {
                title: `Untitled Section ${this.inspectionTestPlan.sections.length + 1}`,
                defaultTitle: `Untitled Section ${this.inspectionTestPlan.sections.length + 1}`,
                tasks: [],
                order: this.inspectionTestPlan.sections.length,
                ref: null,
                custom_ref: null,
                optional: false,
            }
            this.inspectionTestPlan.sections.push(newSection);
            this.inspectionTestPlan.update();
            this.inspectionTestPlan.addSnapshot();
            this.select(this.inspectionTestPlan.sections.length - 1);
        },
        deleteSection(section) {
            this.$modals.confirmDelete({
                title: `Delete section "${section.title}"`,
                button: 'Yes, delete section',
                handle: () => {
                    this.inspectionTestPlan.sections = this.inspectionTestPlan.sections.filter((s) => s !== section)
                    this.inspectionTestPlan.update()
                    this.inspectionTestPlan.addSnapshot();
                    if (this.inspectionTestPlan.sections.length) {
                        this.select(Math.min(this.activeSectionIndex, this.inspectionTestPlan.sections.length - 1))
                    } else {
                        this.selectPage('cover')
                    }
                }
            })
        },
        duplicateSection(section) {
            this.inspectionTestPlan.sections.splice(this.inspectionTestPlan.sections.indexOf(section), 0, cloneDeep(section));
            this.select(Math.min(this.activeSectionIndex + 1, this.inspectionTestPlan.sections.length - 1))
            this.inspectionTestPlan.update()
            this.inspectionTestPlan.addSnapshot();
            this.resizeTabs();
        },
        select(sectionIndex) {
            this.resizeTabs();
            this.$emit('update:active-section-index', sectionIndex)
            this.activeSectionIndex = sectionIndex
        },
        isPage(page) {
            return this.activeSectionIndex === null && this.activePage === page
        },
        selectPage(page) {
            this.resizeTabs();
            this.activeSectionIndex = null;
            this.activePage = page;
            this.$emit('update:active-section-index', null)
            this.$emit('update:active-page', page)
        },
        sectionsReordered(evt) {
            if (evt.moved) {
                if (evt.moved.oldIndex === this.activeSectionIndex) {
                    this.select(evt.moved.newIndex)
                } else if ((evt.moved.oldIndex - this.activeSectionIndex) * (evt.moved.newIndex - this.activeSectionIndex) <= 0) {
                    if (evt.moved.oldIndex > evt.moved.newIndex) {
                        this.select(this.activeSectionIndex + 1);
                    } else {
                        this.select(this.activeSectionIndex - 1);
                    }
                }
                this.inspectionTestPlan.update()
                this.resizeTabs();
                this.inspectionTestPlan.addSnapshot();
            }
        },
        resizeTabs() {
            if (this.editable) {
                this.$nextTick(() => {
                    this.$refs.resizer.forEach((e, i) => e.resizeInput(e.$el));
                })
            }
        },
        toggleOptional(section, close) {
            section.optional = !section.optional
            close()
            this.inspectionTestPlan.update();
            this.inspectionTestPlan.addSnapshot();
        }
    },
    beforeUpdate() {
        this.resizeTabs();
        if (this.activeSectionIndex + 1 > this.inspectionTestPlan.sections.length) {
            if (this.inspectionTestPlan.sections.length) {
                this.select(this.inspectionTestPlan.sections.length - 1)
            } else {
                this.selectPage('properties')
            }
        }
    }
}
</script>
